import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { t } from '../../../utilities/translator';

const AudioRecorder = ({
  isRecording,
  setIsRecording,
  onFinishRecording,
  showLoader,
  setError,
}) => {
  const [recordingLength, setRecordingLength] = useState(0);
  const [volumeData, setVolumeData] = useState(new Array(40).fill({ volume: 0, default: true }));
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const volumeIntervalRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBlobRef = useRef(null);

  const handleToggleRecording = async () => {
    try {
      setVolumeData(new Array(40).fill({ volume: 0, default: true }));
      setRecordingLength(0);
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: 16000 } });
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        audioBlobRef.current = audioBlob; // Save audioBlob for sending later
        if (recordingLength < 1) {
          audioBlobRef.current = null; // Clear audioBlob if recording is too short
          return;
        }
        audioChunksRef.current = [];
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);

      // Set a timeout to stop recording after 60 seconds
      timeoutRef.current = setTimeout(() => {
        stopRecording();
        setError(t('onboarding.assistant.errorRecordLength'));
      }, 60000);

      // Start the interval to track recording length
      intervalRef.current = setInterval(() => {
        setRecordingLength(prevLength => prevLength + 1);
      }, 1000);

      const source = audioContextRef.current.createMediaStreamSource(stream);
      audioAnalyserRef.current = audioContextRef.current.createAnalyser();
      source.connect(audioAnalyserRef.current);
      audioAnalyserRef.current.fftSize = 512;
      const bufferLength = audioAnalyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      volumeIntervalRef.current = setInterval(() => {
        audioAnalyserRef.current.getByteFrequencyData(dataArrayRef.current);
        const averageVolume = dataArrayRef.current.reduce((sum, value) => sum + value, 0) / bufferLength;
        let volumeLevel = Math.min(Math.max(Math.round((averageVolume / 255) * 100), 1), 100);
        if (volumeLevel <= 5) volumeLevel = 1;
        setVolumeData((prevVolumeData) => {
          const newVolumeData = [...prevVolumeData, { volume: volumeLevel, default: false }];
          if (newVolumeData.length > 40) {
            newVolumeData.shift();
          }
          return newVolumeData;
        });
      }, 25);
    } catch (err) {
      console.error('Error accessing microphone', err);
    }
  };

  useEffect(() => {
    if (!isRecording) {
      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
      }
    }
  }, [isRecording]);

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (recordingLength < 1) {
      setRecordingLength(0);
      audioBlobRef.current = null; // Clear audioBlob if recording is too short
    }

    if (mediaStreamRef.current) {
      // Stop all tracks to release the microphone
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
    }

    setIsRecording(false);
  };

  const sendAudio = async () => {
    stopRecording();
    mediaRecorderRef.current.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
      audioBlobRef.current = audioBlob; // Save audioBlob for sending later
      if (recordingLength < 1) {
        audioBlobRef.current = null; // Clear audioBlob if recording is too short
        return;
      }
      audioChunksRef.current = [];
      onFinishRecording(audioBlobRef.current)
    }
  };

  const handleCancelClick = () => {
    stopRecording();
    setIsRecording(false);
  }

  return (
    <>
      {!isRecording && (
        <div className={cn('record-btn', { active: isRecording, loading: showLoader })} onClick={handleToggleRecording}>
          <i className={`${showLoader ? 'ri-loader-fill' : 'ri-mic-line'} text-xl leading-none`} />
        </div>
      )}

      {(isRecording) && (
        <div className="blob-element">
          <button onClick={handleCancelClick} className="blob-element__cancel">
            <i className="ri-close-line" />
          </button>
          <div className="blob-bars">
            {volumeData.map((item, index) => (
              <div
                key={index}
                className={cn('blob-bars__bar', { default: item.default })}
                style={{ height: `${6 + item.volume}px` }}
              />
            ))}
          </div>
          {Math.floor(recordingLength / 60)}:{('0' + (recordingLength % 60)).slice(-2)}
          <button onClick={sendAudio} className="blob-element__send">
            <i className="ri-check-line text-xl" />
          </button>
        </div>
      )}
    </>
  );
};

export default AudioRecorder;
